import { OrderRow } from '../types/app';

export type OrderPaymentAdjustment =
  | { kind: 'even' }
  | { kind: 'refund'; amount: number }
  | { kind: 'collect'; amount: number };

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getAmountPaidAtCheckout(order: OrderRow): number {
  const stored = Number(order.amount_paid_at_checkout);
  if (Number.isFinite(stored) && stored > 0) return roundMoney(stored);
  if (order.payment_status === 'paid') return roundMoney(Number(order.total_amount) || 0);
  return 0;
}

export function getOutstandingBalance(order: OrderRow): number {
  const paid = getAmountPaidAtCheckout(order);
  const total = roundMoney(Number(order.total_amount) || 0);
  return roundMoney(Math.max(0, total - paid));
}

export function computeOrderPaymentAdjustment(order: OrderRow, newTotal: number): OrderPaymentAdjustment {
  const paid = getAmountPaidAtCheckout(order);
  const total = roundMoney(newTotal);
  const delta = roundMoney(total - paid);
  if (Math.abs(delta) < 0.01) return { kind: 'even' };
  if (delta < 0) return { kind: 'refund', amount: roundMoney(Math.abs(delta)) };
  return { kind: 'collect', amount: delta };
}

export function formatPaymentAdjustmentMessage(
  adjustment: OrderPaymentAdjustment,
  order: OrderRow
): string {
  if (adjustment.kind === 'even') {
    return 'No additional payment needed after this change.';
  }
  if (adjustment.kind === 'refund') {
    return `Refund £${adjustment.amount.toFixed(2)} to the customer (cash is fine).`;
  }
  const paid = getAmountPaidAtCheckout(order);
  if (paid > 0) {
    return `Collect an additional £${adjustment.amount.toFixed(2)} when the customer arrives (cash or card).`;
  }
  return `Collect £${adjustment.amount.toFixed(2)} when the customer arrives (cash or card).`;
}

export function formatOutstandingBalanceMessage(order: OrderRow, outstanding: number): string {
  const paid = getAmountPaidAtCheckout(order);
  if (paid > 0) {
    return `Customer already paid £${paid.toFixed(2)}. Collect the remaining £${outstanding.toFixed(2)} in cash or by card when they arrive.`;
  }
  return `Collect £${outstanding.toFixed(2)} in cash or by card when the customer arrives.`;
}
